import { Component, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { forkJoin, of, Subject } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { MerchantWithdrawFormComponent } from './merchant-withdraw-form/merchant-withdraw-form.component';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { RiskGroupShortCodeEnum } from '@olv/ui-shared/enums/risk-group-short-code.enum';
import { RiskLevelEnum } from '@olv/ui-shared/enums/task.enums';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import {
  CurrencyOption,
  WalletDto,
  WalletTypeEnum,
  WhitelistedWalletType,
  WalletStatusEnum,
  WhitelistedWalletStatusEnum,
  WalletOwnerTypeEnum,
  WithdrawalTypeEnum,
  CurrencyBalances,
} from '@olv/ui-shared/models/wallet.model';
import { WithdrawalType } from '@olv/ui-shared/enums/withdrawal-type.enum';
import { MerchantTransactionService } from '@olv/ui-shared/services/merchant-transaction.service';
import { MerchantRiskGroup } from '@olv/ui-shared/models/merchant.model';
import { MerchantService, WhitelistedWalletResponseDto } from '@olv/ui-shared/services/merchant.service';
import { PillSearchComponent, SearchState } from '@olv/ui-shared/ui-shared/pill-search/pill-search.component';
import { SuccessViewComponent } from '@olv/ui-shared/ui-shared/success-view/success-view.component';
import { SummaryViewComponent } from '@olv/ui-shared/ui-shared/summary-view/summary-view.component';
import { WalletCardComponent, walletCardHeight } from '@olv/ui-shared/ui-shared/wallet-card/wallet-card.component';
import {
  SourcingWalletTreeComponent,
  SourcingTreeDestinationWallet,
} from '@olv/ui-shared/ui-shared/sourcing-wallet-tree/sourcing-wallet-tree.component';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { DisplayDetails } from '@olv/ui-shared/models/display-details.interface';
import { SourcingWalletDto } from '@olv/ui-shared/models/sourcing-wallet.model';
import { WithdrawalSourcingMode } from '@olv/ui-shared/enums/withdrawal-sourcing-mode.enum';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { VirtualScrollComponent } from '@olv/ui-shared/ui-shared/virtual-scroll/virtual-scroll.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OtpVerificationDialogComponent } from '@olv/ui-shared/ui-shared/otp-verification-dialog/otp-verification-dialog.component';
import { PrepareTransactionRequestDto } from '@olv/ui-shared/models/merchant-transaction.model';

interface WithdrawalDetails {
  withdrawalType: string;
  sourceWalletId: string;
  sourceWalletAddress: string;
  receiverAddress?: string;
  receiverWalletId?: string;
  currency: string;
  currencyId: string;
  amount: number;
  networkType: number;
  exchangeRate?: number;
  convertedAmount?: number;
  baseCurrencySymbol?: string;
  baseCurrencyPrecision?: number;
  currencyPrecision?: number;
  targetRiskGroup?: RiskGroupShortCodeEnum;
}

interface InitialFormData {
  blockchain: number;
  currency: string;
  currencyId: string;
  currencyOptions: CurrencyOption[];
  routingMode?: 'automatic' | 'wallet';
  riskGroup?: RiskGroupShortCodeEnum;
}

interface withdrawalFormData {
  withdrawalType: string;
  receiverWalletId: string;
  receiverAddress: string;
  amount: number;
  exchangeRate: number;
  convertedAmount: number;
  baseCurrencySymbol: string;
  baseCurrencyPrecision: number;
  currencyPrecision: number;
}

@Component({
  selector: 'app-merchant-withdraw',
  standalone: true,
  imports: [
    MerchantWithdrawFormComponent,
    PillSearchComponent,
    WalletCardComponent,
    SummaryViewComponent,
    SuccessViewComponent,
    HasPermissionDirective,
    LoadingSpinnerComponent,
    VirtualScrollComponent,
    SourcingWalletTreeComponent
],
  templateUrl: './merchant-withdraw.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./merchant-withdraw.component.scss'],
})
export class MerchantWithdrawComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private transactionService = inject(MerchantTransactionService);
  private merchantService = inject(MerchantService);

  protected readonly walletCardHeight = walletCardHeight;
  @Input() wallets: WalletDto[] = [];
  @Input() isLoading = false;
  @Input() error: string | null = null;
  @Input() totalBalance: CurrencyBalances[] = [];
  @Input() isMultiRiskPaymentsEnabled = false;
  @Input() riskGroups: MerchantRiskGroup[] = [];

  readonly WithdrawalType = WithdrawalType;
  private readonly appCtx = inject(AppContextService);
  private readonly metadata = inject(NetworkMetadataService);

  searchText = '';
  filteredWallets: WalletDto[] = [];
  payoutWallets: WalletDto[] = [];
  internalWallets: WalletDto[] = [];
  private payoutWalletsBackup: WalletDto[] = [];
  isSettlementApp = false; // Flag to indicate if the current app is merchant or not

  systemWallets: WalletDto[] = [];

  allMerchantWallets: WalletDto[] = [];

  merchantId = '';
  selectedSourceWallet: WalletDto | null = null;
  selectedCurrency: string | null = '';
  selectedDestinationWallet: WalletDto | null = null;
  selectedBlockchainFilter: number | null = null;
  selectedWithdrawalType: WhitelistedWalletType | null = null;
  routingMode: 'automatic' | 'wallet' = 'automatic';
  viewState: 'initial' | 'withdrawal-type' | 'summary' | 'success' = 'initial';
  stepForm: 'initial' | 'withdrawal' = 'initial';

  initialFormData: InitialFormData | null = null;
  withdrawalDetails: WithdrawalDetails | null = null;
  transactionId: string | null = null;
  transactionError: string | null = null;

  sourceWallet?: SourcingWalletDto;
  sourcingWallets: SourcingWalletDto[] = [];
  isLoadingSourcing = false;
  automaticAvailableBalance: number | null = null;

  displayDetails: DisplayDetails = {
    currency: '',
    sourceWallet: '',
    sourceWalletName: '',
    amount: 0,
    targetWallet: '',
    targetWalletName: '',
    exchangeRate: 0,
    convertedAmount: 0,
    baseCurrencySymbol: '',
    baseCurrencyPrecision: 0,
    currencyPrecision: 0,
  };

  readonly WalletStatusEnum = WalletStatusEnum;

  private readonly destroy$ = new Subject<void>();
  private dialog = inject(MatDialog);

  ngOnInit(): void {
    this.metadata.load();
    const appName = this.appCtx.getAppName();
    this.isSettlementApp = appName === 'settlement';

    if (this.isSettlementApp) {
      this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        this.merchantId = params.get('id')!;
      });
      this.wallets = this.wallets.filter(
        (wallet) =>
          wallet.isActive && wallet.balance?.currencyBalances?.some((balance) => balance.totalAmountInCurrency > 0),
      );
    } else {
      // The merchant portal has no source-wallet selection, so it defaults to manual
      // destination routing; automatic routing is opt-in via the toggle.
      this.routingMode = 'wallet';
      this.loadDestinationWallets();
    }

    this.applyFilter();
  }

  ngOnChanges(): void {
    this.applyFilter();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadDestinationWallets(): void {
    this.isLoading = true;

    const filters = {
      walletStatuses: [WhitelistedWalletStatusEnum.Approved],
      ...(this.isSettlementApp && { merchantIdFilter: this.merchantId }),
    };

    this.metadata
      .whenLoaded()
      .pipe(
        filter(() => !this.metadata.error()),
        switchMap(() => this.merchantService.getWhitelistedWallets(filters)),
        map((response) => response.items.map((item) => this.mapWhitelistedToWalletDto(item))),
        map((wallets) => {
          if (this.isSettlementApp && this.selectedSourceWallet) {
            return wallets.filter((w) => w.networkType === this.selectedSourceWallet?.networkType);
          }
          return wallets;
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (wallets) => {
          this.payoutWallets = [...wallets];
          this.payoutWalletsBackup = [...wallets];
          this.applyFilter();
        },
        error: (err) => console.error('Error loading destination wallets (payout):', err),
      });
  }

  private mapWhitelistedToWalletDto(input: WhitelistedWalletResponseDto): WalletDto {
    const chain = input.blockchain.toUpperCase();

    const network = this.metadata
      .networks()
      .find((n) => n.code.toUpperCase() === chain || n.name.toUpperCase() === chain);

    if (!network) console.error('Unknown whitelisted wallet blockchain:', input.blockchain);

    const riskLevelMap: Record<string, RiskLevelEnum> = {
      'Not Assessed': RiskLevelEnum.NotAssessed,
      Unknown: RiskLevelEnum.Unknown,
      Low: RiskLevelEnum.Low,
      Medium: RiskLevelEnum.Medium,
      High: RiskLevelEnum.High,
      Severe: RiskLevelEnum.Severe,
    };

    return {
      id: input.id,
      address: input.walletAddress,
      networkId: '',
      networkType: network?.networkType ?? 0,
      ownerType: input.ownerType === 1 ? WalletOwnerTypeEnum.Merchant : WalletOwnerTypeEnum.System,
      merchantId: undefined,
      merchantName: input.merchantName,
      walletName: input.walletName,
      walletType: undefined,
      systemWalletType: null,
      publicKeyId: undefined,
      publicKey: null,
      isExternalWallet: false,
      status: WalletStatusEnum.Active,
      riskLevel: riskLevelMap[input.riskLevel] ?? RiskLevelEnum.NotAssessed,
      riskScore: undefined,
      withdrawalType: input.withdrawalType,
      isActive: true,
      balance: {
        totalBalanceInBaseCurrency: 0,
        availableBalanceInBaseCurrency: 0,
        baseCurrency: '$',
        baseCurrencyDisplayPrecision: 2,
        currencyBalances: [],
      },
      createdAt: new Date(input.date),
      updatedAt: new Date(input.date),
      isSweepingEnabled: false,
    };
  }

  onSearchChange(searchState: SearchState): void {
    this.searchText = searchState.value;
    this.applyFilter();
  }

  onBlockchainFilterChanged(networkType: number | null): void {
    if (this.isSettlementApp) {
      this.selectedSourceWallet = null;
    } else {
      if (this.selectedDestinationWallet && this.selectedDestinationWallet.networkType !== networkType) {
        this.selectedDestinationWallet = null;
      }
    }

    this.selectedBlockchainFilter = networkType;
    this.applyFilter();
  }

  onCurrencyFilterChanged(currency: string | null): void {
    this.selectedCurrency = currency;
    this.applyFilter();
  }

  onRoutingModeChanged(mode: 'automatic' | 'wallet'): void {
    this.routingMode = mode;
    if (mode === 'automatic') {
      this.selectedSourceWallet = null;
    }
    this.applyFilter();
  }

  onWithdrawalTypeChanged(withdrawalType: string | null): void {
    this.searchText = '';
    this.selectedWithdrawalType = withdrawalType as WhitelistedWalletType | null;
    this.selectedDestinationWallet = null;
    this.applyFilter();
  }

  private applyFilter(): void {
    const search = this.searchText.toLowerCase().trim();

    if (this.viewState === 'initial') {
      let filtered = this.isSettlementApp ? [...this.wallets] : [...this.payoutWalletsBackup];

      if (this.selectedBlockchainFilter !== null) {
        filtered = filtered.filter((w) => w.networkType === this.selectedBlockchainFilter);
      }

      const result = search ? filtered.filter((w) => this.matchSearch(w, search)) : filtered;
      if (this.isSettlementApp) {
        this.filteredWallets = result;
      } else {
        this.payoutWallets = result;
      }
      return;
    }

    if (this.viewState === 'withdrawal-type') {
      let pool: WalletDto[] = [...this.payoutWalletsBackup];

      // remove any onetime wallets
      pool = pool.filter((w) => w.walletType !== WalletTypeEnum.OnetimeUse);

      if (this.selectedBlockchainFilter !== null) {
        pool = pool.filter((w) => w.networkType === this.selectedBlockchainFilter);
      }
      if (this.isSettlementApp && this.selectedWithdrawalType !== null) {
        const withdrawalTypeLabels: Record<WhitelistedWalletType, string> = {
          [WhitelistedWalletType.SettlementPayout]: 'Settlement Payout',
          [WhitelistedWalletType.ClientPayout]: 'Client Payout',
          [WhitelistedWalletType.ServicePayout]: 'Service Payout',
        };
        const label = withdrawalTypeLabels[this.selectedWithdrawalType];
        pool = pool.filter((w) => w.withdrawalType === label);
      }
      this.payoutWallets = search ? pool.filter((w) => this.matchSearch(w, search)) : pool;

      return;
    }
  }

  private matchSearch(wallet: WalletDto, search: string): boolean {
    const address = wallet.address?.toLowerCase() || '';
    const id = wallet.id?.toLowerCase() || '';
    const name = wallet.walletName?.toLowerCase() || `wallet ${id.substring(0, 6)}`;

    return address.includes(search) || name.includes(search) || id.includes(search);
  }

  onWalletSelected(wallet: WalletDto): void {
    if (this.viewState === 'initial') {
      if (!this.isSettlementApp) {
        this.selectedDestinationWallet = wallet;

        if (this.selectedBlockchainFilter !== wallet.networkType) {
          this.selectedBlockchainFilter = wallet.networkType;
          this.onBlockchainFilterChanged(wallet.networkType);
        }
        return;
      }

      this.selectedSourceWallet = wallet;
      this.selectedBlockchainFilter = wallet.networkType;
    } else if (this.viewState === 'withdrawal-type') {
      this.selectedDestinationWallet = wallet;
    }
  }

  isWalletSelected(walletId: string): boolean {
    if (this.viewState === 'initial') {
      if (this.isSettlementApp) {
        return this.selectedSourceWallet?.id === walletId;
      } else {
        return this.selectedDestinationWallet?.id === walletId;
      }
    }

    if (this.viewState === 'withdrawal-type') {
      return this.selectedDestinationWallet?.id === walletId;
    }
    return false;
  }

  getWalletCurrencies(wallet: WalletDto): { name: string; amount: number; displayPrecision: number }[] {
    if (!wallet?.balance || !wallet.balance.currencyBalances || wallet.balance.currencyBalances.length === 0) {
      return [];
    }

    return wallet.balance.currencyBalances.map((balance) => ({
      name: balance.currencySymbol,
      amount: balance.availableAmountInCurrency,
      displayPrecision: balance.currencyDisplayPrecision,
    }));
  }

  getRiskLevelName(riskLevel?: RiskLevelEnum): string {
    const riskLevelMap: Record<RiskLevelEnum, string> = {
      [RiskLevelEnum.Unsupported]: 'unsupported',
      [RiskLevelEnum.NotAssessed]: 'notassessed',
      [RiskLevelEnum.Unknown]: 'unknown',
      [RiskLevelEnum.Low]: 'low',
      [RiskLevelEnum.Medium]: 'medium',
      [RiskLevelEnum.High]: 'high',
      [RiskLevelEnum.Severe]: 'severe',
    };

    return riskLevel !== undefined ? riskLevelMap[riskLevel] : 'Not Assessed';
  }

  /** A resolved payout wallet is enough to draw the tree: with no contributors it is a direct
   *  sender -> receiver hop, which is still what the operator needs to see (bug 10166). */
  get hasSourcingTree(): boolean {
    return !!this.sourceWallet;
  }

  private destinationWalletForTreeSource: WalletDto | null = null;
  private destinationWalletForTreeCache: SourcingTreeDestinationWallet | undefined = undefined;


  get destinationWalletForTree(): SourcingTreeDestinationWallet | undefined {
    if (this.selectedDestinationWallet === this.destinationWalletForTreeSource) {
      return this.destinationWalletForTreeCache;
    }
    this.destinationWalletForTreeSource = this.selectedDestinationWallet;
    this.destinationWalletForTreeCache = this.selectedDestinationWallet
      ? {
          walletName: this.selectedDestinationWallet.walletName,
          address: this.selectedDestinationWallet.address,
          networkType: this.selectedDestinationWallet.networkType,
          riskLevel:
            this.selectedDestinationWallet.riskLevel !== undefined
              ? this.getRiskLevelName(this.selectedDestinationWallet.riskLevel)
              : undefined,
        }
      : undefined;
    return this.destinationWalletForTreeCache;
  }

  proceedToWithdrawalType(formData: any): void {
    if (!this.isSettlementApp) {
      this.proceedToSummaryFromMerchantPortal(formData);
      return;
    }

    if (this.routingMode === 'wallet' && !this.selectedSourceWallet) {
      console.error('No source wallet selected');
      return;
    }

    if (!formData.currencyId) {
      return;
    }

    this.initialFormData = {
      blockchain: formData.blockchain,
      currency: formData.currency,
      currencyId: formData.currencyId,
      currencyOptions: formData.currencyOptions || [],
      routingMode: formData.routingMode,
      riskGroup: formData.riskGroup,
    };

    this.searchText = '';
    this.stepForm = 'withdrawal';
    this.loadStep2DataThenAdvance();
  }

  private loadStep2DataThenAdvance(): void {
    this.isLoading = true;

    const filters = {
      walletStatuses: [WhitelistedWalletStatusEnum.Approved],
      ...(this.isSettlementApp && { merchantIdFilter: this.merchantId }),
    };

    const wallets$ = this.metadata.whenLoaded().pipe(
      filter(() => !this.metadata.error()),
      switchMap(() => this.merchantService.getWhitelistedWallets(filters)),
      map((response) => response.items.map((item) => this.mapWhitelistedToWalletDto(item))),
      map((wallets) =>
        this.selectedSourceWallet
          ? wallets.filter((w) => w.networkType === this.selectedSourceWallet?.networkType)
          : wallets,
      ),
    );

    const riskGroup = this.initialFormData?.riskGroup ?? null;
    const balances$ =
      this.isAutomaticWithdrawal && riskGroup != null && this.initialFormData?.currencyId
        ? this.transactionService
            .getAutomaticWithdrawalAvailableBalances(this.initialFormData.currencyId, this.merchantId)
            .pipe(catchError(() => of(null)))
        : of(null);

    forkJoin({ wallets: wallets$, balances: balances$ })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: ({ wallets, balances }) => {
          this.payoutWallets = [...wallets];
          this.payoutWalletsBackup = [...wallets];

          if (balances && riskGroup != null) {
            const match = balances.find((b) => b.riskGroupShortCode === riskGroup);
            this.automaticAvailableBalance = match?.cumulativeAvailable ?? 0;
          } else {
            this.automaticAvailableBalance = null;
          }

          this.viewState = 'withdrawal-type';
          this.applyFilter();
        },
        error: (err) => {
          console.error('Error preparing withdrawal step 2:', err);
          this.automaticAvailableBalance = null;
          this.viewState = 'withdrawal-type';
          this.applyFilter();
        },
      });
  }

  private proceedToSummaryFromMerchantPortal(formData: any): void {
    if (!this.selectedDestinationWallet) {
      return;
    }

    if (!formData.currencyId) {
      return;
    }

    this.initialFormData = {
      blockchain: formData.blockchain,
      currency: formData.currency,
      currencyId: formData.currencyId,
      currencyOptions: formData.currencyOptions || [],
    };

    this.withdrawalDetails = {
      withdrawalType: WithdrawalType.ClientPayout.toString(),
      sourceWalletId: '',
      sourceWalletAddress: '',
      receiverAddress: formData.receiverAddress,
      receiverWalletId: formData.receiverWalletId,
      currency: formData.currency,
      currencyId: formData.currencyId,
      amount: formData.amount,
      networkType: formData.blockchain,
      exchangeRate: formData.exchangeRate,
      convertedAmount: formData.convertedAmount,
      baseCurrencySymbol: formData.baseCurrencySymbol,
      baseCurrencyPrecision: formData.baseCurrencyPrecision,
      currencyPrecision: formData.currencyPrecision,
      targetRiskGroup: formData.riskGroup ?? undefined,
    };

    this.displayDetails = {
      currency: formData.currency.toUpperCase(),
      network: this.metadata.network(formData.blockchain)?.name,
      sourceWallet: '',
      sourceWalletName: '',
      amount: formData.amount,
      targetWallet: this.selectedDestinationWallet.address,
      targetWalletName: this.selectedDestinationWallet.walletName,
      exchangeRate: formData.exchangeRate,
      convertedAmount: formData.convertedAmount,
      baseCurrencySymbol: formData.baseCurrencySymbol,
      baseCurrencyPrecision: formData.baseCurrencyPrecision,
      currencyPrecision: formData.currencyPrecision,
    };

    this.viewState = 'summary';

    if (this.showsSourcingTree) {
      this.loadSourcingWallets();
    }
  }

  proceedToSummary(withdrawalFormData: withdrawalFormData): void {
    if (!this.selectedDestinationWallet || !this.initialFormData) {
      console.error('Missing required data for summary');
      return;
    }

    if (this.isSettlementApp && this.routingMode === 'wallet' && !this.selectedSourceWallet) {
      console.error('No source wallet selected');
      return;
    }

    this.withdrawalDetails = {
      withdrawalType: withdrawalFormData.withdrawalType,
      sourceWalletId: this.selectedSourceWallet?.id ?? '',
      sourceWalletAddress: this.selectedSourceWallet?.address ?? '',
      receiverAddress: withdrawalFormData.receiverAddress,
      receiverWalletId: withdrawalFormData.receiverWalletId,
      currency: this.initialFormData.currency,
      currencyId: this.initialFormData.currencyId,
      amount: withdrawalFormData.amount,
      networkType: this.selectedSourceWallet?.networkType ?? this.initialFormData.blockchain,
      exchangeRate: withdrawalFormData.exchangeRate,
      convertedAmount: withdrawalFormData.convertedAmount,
      baseCurrencySymbol: withdrawalFormData.baseCurrencySymbol,
      baseCurrencyPrecision: withdrawalFormData.baseCurrencyPrecision,
      currencyPrecision: withdrawalFormData.currencyPrecision,
      targetRiskGroup: this.initialFormData.riskGroup ?? undefined,
    };

    this.displayDetails = {
      currency: this.initialFormData.currency.toUpperCase(),
      network: this.metadata.network(this.withdrawalDetails.networkType)?.name,
      sourceWallet: this.selectedSourceWallet ? this.selectedSourceWallet.address : '',
      sourceWalletName: this.selectedSourceWallet?.walletName ?? '',
      amount: this.withdrawalDetails.amount,
      targetWallet: this.selectedDestinationWallet.address,
      targetWalletName: this.selectedDestinationWallet.walletName,
      exchangeRate: this.withdrawalDetails.exchangeRate,
      convertedAmount: this.withdrawalDetails.convertedAmount,
      baseCurrencySymbol: this.withdrawalDetails.baseCurrencySymbol,
      baseCurrencyPrecision: this.withdrawalDetails.baseCurrencyPrecision,
      currencyPrecision: this.withdrawalDetails.currencyPrecision,
    };

    this.viewState = 'summary';

    // For automatic routing in the settlement portal, resolve the sourcing
    // wallets that will feed the selected destination wallet and render them as
    // a routing tree alongside the summary.
    if (this.showsSourcingTree) {
      this.loadSourcingWallets();
    }
  }

  private loadSourcingWallets(): void {
    if (!this.showsSourcingTree || !this.selectedDestinationWallet || !this.withdrawalDetails) {
      return;
    }

    this.isLoadingSourcing = true;
    this.sourcingWallets = [];
    this.sourceWallet = undefined;

    this.transactionService
      .getSourcingWallets(
        {
          destinationWalletId: this.selectedDestinationWallet.id,
          destinationWalletAddress: this.selectedDestinationWallet.address,
          currencyId: this.withdrawalDetails.currencyId,
          amount: this.withdrawalDetails.amount,
          networkType: this.selectedDestinationWallet.networkType,
          riskGroup: this.withdrawalDetails.targetRiskGroup,
        },
        this.isSettlementApp ? this.merchantId : undefined,
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingSourcing = false)),
      )
      .subscribe({
        next: (response) => {
          this.sourceWallet = response.sourceWallet;
          this.sourcingWallets = response.sourcingWallets ?? [];

          // The backend resolves the real payout wallet (the on-chain sender) for an
          // automatic withdrawal, so reflect it in the summary instead of the placeholder.
          if (response.sourceWallet) {
            this.displayDetails = {
              ...this.displayDetails,
              sourceWallet: response.sourceWallet.address ?? '',
              sourceWalletName: response.sourceWallet.walletName ?? '',
            };
          }
        },
        error: (err) => console.error('Error loading sourcing wallets:', err),
      });
  }

  openOtp(): void {
    if (!this.withdrawalDetails) {
      return;
    }

    const dialogRef = this.dialog.open(OtpVerificationDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Verify Withdrawal',
      },
    });

    const sub = dialogRef.componentInstance.submitOtp.subscribe((otp: string) => {
      this.onOtpSubmit(otp, dialogRef);
    });

    dialogRef.afterClosed().subscribe(() => {
      sub.unsubscribe();
    });
  }

  private buildTransactionPayload(totp: string): PrepareTransactionRequestDto {
    if (!this.withdrawalDetails) {
      throw new Error('Withdrawal details not available');
    }
    if (!this.withdrawalDetails.currencyId) {
      throw new Error('Currency ID not available');
    }
    const isAutomatic = this.isAutomaticWithdrawal;

    return {
      // Omit when there is no operator-selected source wallet (automatic mode resolves it from the risk group) —
      // sending "" would fail to bind to a Guid on the backend.
      senderWalletId: this.withdrawalDetails.sourceWalletId || undefined,
      receiverAddress: this.withdrawalDetails.receiverAddress,
      // TODO: wallet selection logic receiverWalletId: this.withdrawalDetails.receiverWalletId,
      amount: this.withdrawalDetails.amount,
      currencyId: this.withdrawalDetails.currencyId,
      merchantId: this.merchantId,
      withdrawalType: Number(this.withdrawalDetails.withdrawalType) satisfies WithdrawalTypeEnum,
      memo: 'Merchant withdrawal',
      totp,
      mode: isAutomatic ? WithdrawalSourcingMode.Automatic : WithdrawalSourcingMode.Generic,
      targetRiskGroup: isAutomatic ? this.withdrawalDetails.targetRiskGroup : undefined,
    };
  }

  private onOtpSubmit(otp: string, dialogRef: MatDialogRef<OtpVerificationDialogComponent>): void {
    if (!this.withdrawalDetails) {
      return;
    }

    this.transactionError = null;

    const dialog = dialogRef.componentInstance;
    dialog.setLoadingState(true);
    dialogRef.disableClose = true;

    const payload: PrepareTransactionRequestDto = this.buildTransactionPayload(otp);

    this.transactionService
      .prepareTransaction(payload)
      .pipe(
        finalize(() => {
          dialog.setLoadingState(false);
          dialogRef.disableClose = false;
        }),
      )
      .subscribe({
        next: (response: { transactionId: string }) => {
          this.transactionId = response.transactionId;
          dialogRef.close();
          this.viewState = 'success';
        },
        error: (error) => {
          if (error.isOtpError) {
            dialog.setErrorState(error.error?.message || error.error);
            dialog.otpForm.reset();
            dialog.focusInput(0);
            return;
          }

          dialogRef.close();

          this.transactionError = error.error?.message || error.message || 'Failed to submit withdrawal';
          this.transactionId = null;
          this.viewState = 'success';
        },
      });
  }

  get activeWallets(): WalletDto[] {
    return this.isSettlementApp ? this.filteredWallets : this.payoutWallets;
  }

  get isAutomaticWithdrawal(): boolean {
    return this.isSettlementApp ? this.routingMode === 'automatic' : this.withdrawalDetails?.targetRiskGroup != null;
  }

  get showsSourcingTree(): boolean {
    return this.isSettlementApp && this.isAutomaticWithdrawal;
  }

  goBackToInitial(): void {
    this.viewState = 'initial';
    this.stepForm = 'initial';
  }

  resetToInitialState(): void {
    this.viewState = 'initial';
    this.stepForm = 'initial';
    this.selectedSourceWallet = null;
    this.selectedDestinationWallet = null;
    this.selectedWithdrawalType = null;
    this.routingMode = this.isSettlementApp ? 'automatic' : 'wallet';
    this.initialFormData = null;
    this.withdrawalDetails = null;
    this.transactionId = null;
    this.transactionError = null;
    this.sourceWallet = undefined;
    this.sourcingWallets = [];
    this.isLoadingSourcing = false;
    this.automaticAvailableBalance = null;

    this.selectedBlockchainFilter = null;
    this.selectedCurrency = null;
    this.searchText = '';

    this.applyFilter();
  }

  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
}
